import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Copy, Gem, Ticket } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { activatePromo } from '../../payment/services/promoApi'
import useRushdropStore from '../store/useRushdropStore'
import useCountdown from '../hooks/useCountdown'
import { formatClockTimer } from '../utils/rushdropFormat'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'

// Маппинг ошибок активации на существующие ключи локализации промокодов
const PROMO_ERROR_KEYS = {
  'promo not found': 'promo.errors.notFound',
  'promo expired': 'promo.errors.expired',
  'promo already activated': 'promo.errors.alreadyActivated',
  'promo limit reached': 'promo.errors.limitReached',
  'error backend': 'promo.errors.backend'
}

/**
 * Блок промокода эвента Rush Drop.
 * Окно промокода закрывается раньше эвента — у него свой таймер ends_in_seconds.
 * Активация — штатный POST /api/promo/ через promoApi.
 */
const RushDropPromo = ({ promo }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const loadEvent = useRushdropStore((state) => state.loadEvent)
  const [isActivating, setIsActivating] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const remaining = useCountdown(promo?.ends_in_seconds)

  if (!promo) return null
  // Окно закрылось — блок скрываем
  if (promo.status === 'expired') return null

  const handleActivate = async () => {
    if (isActivating) return
    hapticImpact('medium')
    setIsActivating(true)
    try {
      await activatePromo(promo.code)
      hapticNotify('success')
      toast.success(t('rushdrop.promo.activated', 'Промокод активирован'))
      // Перезапрашиваем эвент, чтобы обновить promo.status
      await loadEvent({ silent: true })
    } catch (error) {
      hapticNotify('error')
      const key = PROMO_ERROR_KEYS[error?.message] || 'promo.errors.backend'
      toast.error(t(key))
    } finally {
      setIsActivating(false)
    }
  }

  const handleCopy = async () => {
    hapticImpact('light')
    try {
      await navigator.clipboard.writeText(promo.code)
      setIsCopied(true)
      toast.success(t('rushdrop.promo.copied', 'Код скопирован'))
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      toast.error(t('rushdrop.promo.copyError', 'Не удалось скопировать код'))
    }
  }

  return (
    <section className="rounded-2xl border border-accent-secondary/30 bg-gradient-to-br from-accent-primary/10 to-accent-secondary/5 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white">
          <Ticket size={16} className="text-accent-secondary" />
          {t('rushdrop.promo.title', 'Промокод эвента')}
        </h2>
        <span className="text-xs tabular-nums text-text-secondary">
          {formatClockTimer(remaining)}
        </span>
      </div>

      <p className="mt-1 text-xs text-text-secondary">
        {t('rushdrop.promo.bonus', '+{{percent}}% к пополнению', { percent: promo.percent })}
      </p>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleCopy}
          className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border border-dashed border-accent-secondary/50 bg-black/30 px-3 py-2.5 font-mono text-base font-bold tracking-widest text-accent-secondary transition-all active:scale-[0.98]"
        >
          <span className="truncate">{promo.code}</span>
          {isCopied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>

      <div className="mt-3">
        {promo.status === 'can_activate' && (
          <button
            type="button"
            onClick={handleActivate}
            disabled={isActivating}
            className="w-full rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2.5 text-sm font-semibold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {isActivating
              ? t('rushdrop.promo.activating', 'Активация…')
              : t('rushdrop.promo.activate', 'Активировать')}
          </button>
        )}

        {promo.status === 'already_active' && (
          <div className="space-y-2">
            <p className="text-center text-xs text-accent-secondary">
              {t('rushdrop.promo.alreadyActive', 'Код активирован — бонус применится к следующему пополнению')}
            </p>
            <button
              type="button"
              onClick={() => {
                hapticImpact('light')
                navigate('/payment')
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2.5 text-sm font-semibold text-dark-primary transition-all active:scale-[0.98]"
            >
              <Gem size={16} />
              {t('rushdrop.promo.topUp', 'Пополнить')}
            </button>
          </div>
        )}

        {promo.status === 'used' && (
          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-text-secondary">
            <Check size={14} className="text-accent-primary" />
            {t('rushdrop.promo.used', 'Бонус получен')}
          </p>
        )}
      </div>
    </section>
  )
}

export default RushDropPromo
